import dbService from "./db.service.js";

const ALL_PLAYLISTS_CACHE_KEY = `playlist`
const OWNED_BY_PLAYLISTS_CACHE_KEY = `playlist_owned_by`
const PLAYLIST_CACHE_KEY = `playlist::[id]`
const PLAYLIST_SONG_CACHE_KEY = `playlist::[id]::song::[songId]`
const ttl = window.TTL_HOUR

const pendingRequest = {}
const abortController = new AbortController()
abortController.signal.addEventListener("abort", () => console.warn('request aborted'))
const cacheEnabled = false;

const PlaylistService = {
    ALL_PLAYLISTS_CACHE_KEY: ALL_PLAYLISTS_CACHE_KEY,
    OWNED_BY_PLAYLISTS_CACHE_KEY: OWNED_BY_PLAYLISTS_CACHE_KEY,
    PLAYLIST_CACHE_KEY: PLAYLIST_CACHE_KEY,
    PLAYLIST_SONG_CACHE_KEY: PLAYLIST_SONG_CACHE_KEY,
    ttl: ttl,
    loadPresentation: async (playlistId) => {

        return fetch('/api/playlist/' + playlistId + '/presentation',
            {
                method: "GET",
                headers: {
                    "content-type": "application/json"
                }
            })

    },
    save: (id, presentation) => {
        return fetch('/api/playlist/' + id + '/presentation',
            {
                method: "POST",
                body: JSON.stringify(presentation),
                headers: {
                    "content-type": "application/json"
                }
            })
    },
    clearCache: (key) => {
        if (window.cacheInstance) {
            if (key) {
                window.cacheInstance.removeItem(key)
            } else {
                window.cacheInstance.clear()
            }
        }
        if (key) {
            dbService.removeItem(key).catch(() => {});
        }
    },
    getCurrentUserId: () => {
        try {
            if (typeof localStorage !== 'undefined') {
                const userRaw = localStorage.getItem('triad_offline_user');
                if (userRaw) {
                    const parsed = JSON.parse(userRaw);
                    return parsed?.id || parsed?._id || parsed?.email || null;
                }
            }
        } catch (_) {
            // ignore localStorage read error
        }
        return null;
    },
    removeCachedPlaylist: (id) => {
        if (!id) return;
        const key = PLAYLIST_CACHE_KEY.replace('[id]', id);
        PlaylistService.clearCache(key);
        if (typeof localStorage !== 'undefined') {
            const currentId = PlaylistService.getCurrentUserId();
            const pagKeys = ['triad_offline_paginated_playlists'];
            const favKeys = ['triad_offline_favorites'];
            if (currentId) {
                pagKeys.push(`triad_offline_paginated_playlists_${currentId}`);
                favKeys.push(`triad_offline_favorites_${currentId}`);
            }
            pagKeys.forEach(pagKey => {
                try {
                    const pagRaw = localStorage.getItem(pagKey);
                    if (pagRaw) {
                        const pag = JSON.parse(pagRaw);
                        if (pag && Array.isArray(pag.data)) {
                            pag.data = pag.data.filter(p => String(p.id || p._id) !== String(id));
                            localStorage.setItem(pagKey, JSON.stringify(pag));
                        }
                    }
                } catch (e) {
                    // Ignore parse error
                }
            });
            favKeys.forEach(favKey => {
                try {
                    const favRaw = localStorage.getItem(favKey);
                    if (favRaw) {
                        const fav = JSON.parse(favRaw);
                        if (Array.isArray(fav)) {
                            const updated = fav.filter(p => String(p.id || p._id) !== String(id));
                            localStorage.setItem(favKey, JSON.stringify(updated));
                        }
                    }
                } catch (e) {
                    // Ignore parse error
                }
            });
        }
    },
    getCachedPlaylist: async (id) => {
        if (!id) return null;
        const key = PLAYLIST_CACHE_KEY.replace('[id]', id);
        if (typeof window !== 'undefined' && window.cacheInstance) {
            const mem = window.cacheInstance.getItem(key);
            if (mem) return mem;
        }
        try {
            const dbItem = await dbService.getItem(key);
            if (dbItem) return dbItem;
        } catch (e) {
            // Ignore IndexedDB read error
        }
        try {
            const pag = PlaylistService.getCachedPaginated(1, '');
            if (pag && Array.isArray(pag.data)) {
                const found = pag.data.find(p => String(p.id || p._id) === String(id));
                if (found) return found;
            }
            const favs = PlaylistService.getCachedFavorites();
            if (Array.isArray(favs)) {
                const found = favs.find(p => String(p.id || p._id) === String(id));
                if (found) return found;
            }
        } catch (e) {
            // Ignore fallback lookup error
        }
        return null;
    },
    getCachedPaginated: (page = 1, search = '', userId = null) => {
        if (page === 1 && !search && typeof localStorage !== 'undefined') {
            try {
                const currentId = userId || PlaylistService.getCurrentUserId();
                if (!currentId) return null;
                // Delete legacy unscoped key to prevent stale cross-user leaks
                localStorage.removeItem('triad_offline_paginated_playlists');
                const raw = localStorage.getItem(`triad_offline_paginated_playlists_${currentId}`);
                return raw ? JSON.parse(raw) : null;
            } catch (e) {
                return null;
            }
        }
        return null;
    },
    getCachedFavorites: (userId = null) => {
        if (typeof localStorage !== 'undefined') {
            try {
                const currentId = userId || PlaylistService.getCurrentUserId();
                if (!currentId) return null;
                // Delete legacy unscoped key to prevent stale cross-user leaks
                localStorage.removeItem('triad_offline_favorites');
                const raw = localStorage.getItem(`triad_offline_favorites_${currentId}`);
                return raw ? JSON.parse(raw) : null;
            } catch (e) {
                return null;
            }
        }
        return null;
    },
    clearDashboardCache: (userId = null) => {
        if (typeof localStorage !== 'undefined') {
            try {
                const currentId = userId || PlaylistService.getCurrentUserId();
                localStorage.removeItem('triad_offline_paginated_playlists');
                localStorage.removeItem('triad_offline_favorites');
                if (currentId) {
                    localStorage.removeItem(`triad_offline_paginated_playlists_${currentId}`);
                    localStorage.removeItem(`triad_offline_favorites_${currentId}`);
                }
            } catch (e) {
                // Ignore localStorage errors
            }
        }
        if (window.cacheInstance) {
            window.cacheInstance.removeItem(ALL_PLAYLISTS_CACHE_KEY);
            window.cacheInstance.removeItem(OWNED_BY_PLAYLISTS_CACHE_KEY);
        }
        dbService.removeItem(ALL_PLAYLISTS_CACHE_KEY).catch(() => {});
        dbService.removeItem(OWNED_BY_PLAYLISTS_CACHE_KEY).catch(() => {});
    },
    notifyPlaylistChanged: (id, fallbackObj) => {
        // Publica snapshot da playlist via transport dual (Ably + WebRTC) para palco
        // offline (Fase 2b). Sem isso, edições só chegam via Go/Ably (nuvem).
        // Regras de segurança: só com songs[] não-vazio (nunca propaga playlist esvaziada
        // por engano) e limite de 200KB (Ably/DataChannel).
        const run = async () => {
            try {
                const [{ default: playlistStore }, { realtimeService }, { default: userStore }] = await Promise.all([
                    import("../store/playlist.store.js"),
                    import("./realtime.service.js"),
                    import("../store/user.store.js")
                ]);
                const st = playlistStore.state.playlist;
                let source = null;
                if (st && String(st.id || st._id) === String(id) && Array.isArray(st.songs) && st.songs.length > 0) {
                    source = st;
                } else if (fallbackObj && Array.isArray(fallbackObj.songs) && fallbackObj.songs.length > 0) {
                    source = fallbackObj;
                }
                if (!source) return;
                let json = '';
                try { json = JSON.stringify(source); } catch (e) { return; }
                if (json.length > 200 * 1024) {
                    console.log('[playlist.service] snapshot grande demais para realtime, pulando broadcast');
                    return;
                }
                const cUid = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
                realtimeService.publish(`playlist::room::${id}`, 'playlist-update-snapshot', {
                    playlistId: id,
                    playlist: source,
                    ts: Date.now(),
                    sender: cUid,
                    sender_client_id: (typeof window !== 'undefined' && window.myClientId) || null
                }).catch(() => {});
            } catch (e) { /* best-effort, ignore */ }
        };
        run();
    },
    withSnapshotNotify: (id, promise, fallbackObj) => {
        // Dispara snapshot após settle (sucesso OU falha offline com estado otimista local).
        try {
            promise.then(
                () => PlaylistService.notifyPlaylistChanged(id, fallbackObj),
                () => PlaylistService.notifyPlaylistChanged(id, fallbackObj)
            );
        } catch (e) { /* best-effort, ignore */ }
        return promise;
    },
    invalidateServiceWorkerCache: (playlistId) => {
        // Remove GET /api/playlist/:id do triad-content-cache do SW.
        // Sem isso, o SW serve StaleWhileRevalidate stale (ignoreSearch:true) e reverte o PATCH_REALTIME_USER.
        try {
            if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'INVALIDATE_API_CACHE',
                    pattern: playlistId ? `/api/playlist/${playlistId}` : '/api/playlist/'
                });
            }
        } catch (e) {
            console.warn('[playlist.service] SW invalidate failed', e);
        }
    },
    loadFallback: async (key, fallback, signal) => {
        try {
            const httpRequest = fallback(signal);
            const response = await httpRequest
            if (signal && signal.aborted) {
                throw new DOMException('Aborted', 'AbortError')
            }
            if (response && response.ok) {
                const data = await response.json();
                const isSong = key.includes('::song::') || key.startsWith('song::');
                const isPlaylist = key.includes('playlist::');
                if (window.cacheInstance) {
                    window.cacheInstance.setItem(key, data, ttl);
                }
                if (isSong || isPlaylist) {
                    await dbService.setItem(key, data);
                }
                if (pendingRequest[key]) {
                    pendingRequest[key]--;
                }
                return data;
            } else {
                if (window.cacheInstance) {
                    const cached = window.cacheInstance.getItem(key);
                    if (cached) return cached;
                }
                const dbCached = await dbService.getItem(key);
                if (dbCached) return dbCached;
                throw new Error(response ? `HTTP ${response.status}` : 'Fetch failed');
            }
        } catch (err) {
            if (window.cacheInstance) {
                const cached = window.cacheInstance.getItem(key);
                if (cached) return cached;
            }
            const dbCached = await dbService.getItem(key);
            if (dbCached) return dbCached;
            throw err;
        }
    },
    loadFromCache: async (key, force, fallback, signal) => {
        if (!cacheEnabled) {
            return PlaylistService.loadFallback(key, fallback, signal)
        }
        return new Promise((resolve, reject) => {
            try {
                let data = window.cacheInstance.getItem(key);
                if (!data || force) {
                    // Do not clear cache preventively! We fetch first.
                    // If the fetch succeeds, loadFallback will overwrite the cache.
                    // If it fails, the old cache remains.
                    PlaylistService.loadFallback(key, fallback, signal)
                        .then(resolve)
                        .catch(reject)
                } else {
                    console.log("Cache hit", key)
                    resolve(data)
                }
            } catch (e) {
                PlaylistService.loadFallback(key, fallback, signal)
                    .then(resolve)
                    .catch(reject)
            }
        });
    },
    create: (playlist) => {
        return fetch('/api/playlist/', {
            method: "POST",
            body: JSON.stringify(playlist),
            headers: {
                "content-type": "application/json"
            }
        })
    },
    fetchPlaylist: (params) => {

        const key = PLAYLIST_CACHE_KEY.replace("[id]", params.id)

        return PlaylistService.loadFromCache(key, params.force, (signal) => {
            return fetch("/api/playlist/" + params.id, {
                signal: signal || abortController.signal,
                method: "GET",
                // force:true (ex: reload após designation) bypassa o SW (ver isFastContentRoute no-store)
                // para não receber GET /api/playlist/:id stale e reverter o PATCH_REALTIME_USER.
                cache: params.force ? 'no-store' : 'default',
                headers: {
                    "content-type": "application/json"
                }
            });
        }, params.signal)
    },
    fetchSong: (params) => {

        const key = PLAYLIST_SONG_CACHE_KEY
            .replace("[id]", params.id)
            .replace("[songId]", params.songId);

        let url = "/api/playlist/" + params.id + '/song/' + params.songId;
        const queryParams = [];
        if (params.process !== undefined && params.process !== 'undefined') {
            queryParams.push("process=" + params.process);
        }
        if (params.realtimeStatus) {
            queryParams.push("realtimeStatus=true");
        }
        if (queryParams.length > 0) {
            url += "?" + queryParams.join("&");
        }

        return PlaylistService.loadFromCache(key, params.force, (signal) => {
            return fetch(url, {
                signal: signal || abortController.signal,
                method: "GET",
                // Leitura autoritativa (force, ex: reconcile de transpose) bypassa o SW
                // para não renderizar conteúdo/tons stale do triad-content-cache.
                cache: (params.force || params.realtimeStatus) ? 'no-store' : 'default',
                headers: {
                    "content-type": "application/json"
                }
            });
        }, params.signal)
    },

    deletePlaylist: (id) => {

        return fetch("/api/playlist/" + id, {
            signal: abortController.signal,
            method: "DELETE",
            headers: {
                "content-type": "application/json"
            }
        })

    },
    fetchAll: (params) => {

        return PlaylistService.loadFromCache(ALL_PLAYLISTS_CACHE_KEY, params?.force, () => {
            return fetch("/api/playlist/", {
                signal: abortController.signal,
                method: "GET",
                cache: "no-store",
                headers: {
                    "content-type": "application/json"
                }
            });
        })
    },
    fetchPaginated: async (params) => {
        const page = params?.page || 1;
        const size = params?.size || 10;
        const sort = params?.sort || '-created_at';
        const search = params?.search || '';
        let url = `/api/playlist/paginated?page=${page}&size=${size}&sort=${sort}`;
        if (search) {
            url += `&search=${encodeURIComponent(search)}`;
        }
        try {
            const response = await fetch(url, {
                signal: abortController.signal,
                method: "GET",
                cache: "no-store",
                headers: {
                    "content-type": "application/json"
                }
            });
            if (response.status === 401) {
                // Sessão inválida: deixa o handler global redirecionar ao login.
                const err = new Error('Unauthorized');
                err.code = 'UNAUTHORIZED';
                throw err;
            }
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const resData = await response.json();
            let result;
            if (resData && Array.isArray(resData.data)) {
                result = resData;
            } else if (Array.isArray(resData)) {
                result = { data: resData, totalPages: 1, total: resData.length };
            } else {
                result = { data: [], totalPages: 0, total: 0 };
            }
            if (page === 1 && !search) {
                const currentId = PlaylistService.getCurrentUserId();
                if (currentId) {
                    if ((result.data || []).length > 0) {
                        localStorage.setItem(`triad_offline_paginated_playlists_${currentId}`, JSON.stringify(result));
                    } else {
                        localStorage.removeItem(`triad_offline_paginated_playlists_${currentId}`);
                    }
                }
                localStorage.removeItem('triad_offline_paginated_playlists');
            }
            // Prime in-memory and IndexedDB cache for each playlist without wiping existing songs
            for (const p of (result.data || [])) {
                const pid = p.id || p._id;
                if (pid) {
                    const pKey = PLAYLIST_CACHE_KEY.replace('[id]', pid);
                    let existing = null;
                    if (window.cacheInstance) {
                        existing = window.cacheInstance.getItem(pKey);
                    }
                    if (!existing) {
                        try {
                            existing = await dbService.getItem(pKey);
                        } catch (e) {
                            // Ignore IndexedDB read error
                        }
                    }
                    const existingHasFullSongs = existing && Array.isArray(existing.songs) && existing.songs.length > 0 &&
                        existing.songs.some(s => (s.data?.title && s.data.title.trim() !== '') || (s.title && s.title.trim() !== '') || s.data?.content || s.data?.lyric || s.modifiedLyric);
                    const pHasFullSongs = Array.isArray(p.songs) && p.songs.length > 0 &&
                        p.songs.every(s => (s.data?.title && s.data.title.trim() !== '') || (s.title && s.title.trim() !== '') || s.data?.content || s.data?.lyric || s.modifiedLyric);

                    if (existingHasFullSongs && !pHasFullSongs) {
                        const merged = { ...existing, ...p, songs: existing.songs };
                        if (window.cacheInstance) {
                            window.cacheInstance.setItem(pKey, merged, ttl);
                        }
                        dbService.setItem(pKey, merged).catch(() => {});
                    } else if (pHasFullSongs || !existing) {
                        if (window.cacheInstance) {
                            window.cacheInstance.setItem(pKey, p, ttl);
                        }
                        dbService.setItem(pKey, p).catch(() => {});
                    }
                }
            }
            return result;
        } catch (error) {
            if (error && error.code === 'UNAUTHORIZED') {
                throw error;
            }
            console.warn("fetchPaginated offline fallback:", error);
            if (page === 1 && !search) {
                const currentId = PlaylistService.getCurrentUserId();
                const cached = currentId ? localStorage.getItem(`triad_offline_paginated_playlists_${currentId}`) : null;
                if (cached) {
                    try {
                        return JSON.parse(cached);
                    } catch (e) {
                        console.error("Error parsing cached playlists:", e);
                    }
                }
            }
            throw error;
        }
    },
    fetchFavorites: async () => {
        try {
            const response = await fetch('/api/playlist/favorites', {
                signal: abortController.signal,
                method: "GET",
                cache: "no-store",
                headers: {
                    "content-type": "application/json"
                }
            });
            if (response.status === 401) {
                const err = new Error('Unauthorized');
                err.code = 'UNAUTHORIZED';
                throw err;
            }
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            const currentId = PlaylistService.getCurrentUserId();
            if (Array.isArray(data)) {
                if (currentId) {
                    if (data.length > 0) {
                        localStorage.setItem(`triad_offline_favorites_${currentId}`, JSON.stringify(data));
                    } else {
                        localStorage.removeItem(`triad_offline_favorites_${currentId}`);
                    }
                }
                localStorage.removeItem('triad_offline_favorites');
            }
            if (Array.isArray(data) && data.length > 0) {
                for (const p of data) {
                    const pid = p.id || p._id;
                    if (pid) {
                        const pKey = PLAYLIST_CACHE_KEY.replace('[id]', pid);
                        let existing = null;
                        if (window.cacheInstance) {
                            existing = window.cacheInstance.getItem(pKey);
                        }
                        if (!existing) {
                            try {
                                existing = await dbService.getItem(pKey);
                            } catch (e) {
                                // Ignore IndexedDB read error
                            }
                        }
                        const existingHasFullSongs = existing && Array.isArray(existing.songs) && existing.songs.length > 0 &&
                            existing.songs.some(s => (s.data?.title && s.data.title.trim() !== '') || (s.title && s.title.trim() !== '') || s.data?.content || s.data?.lyric || s.modifiedLyric);
                        const pHasFullSongs = Array.isArray(p.songs) && p.songs.length > 0 &&
                            p.songs.every(s => (s.data?.title && s.data.title.trim() !== '') || (s.title && s.title.trim() !== '') || s.data?.content || s.data?.lyric || s.modifiedLyric);

                        if (existingHasFullSongs && !pHasFullSongs) {
                            const merged = { ...existing, ...p, songs: existing.songs };
                            if (window.cacheInstance) {
                                window.cacheInstance.setItem(pKey, merged, ttl);
                            }
                            dbService.setItem(pKey, merged).catch(() => {});
                        } else if (pHasFullSongs || !existing) {
                            if (window.cacheInstance) {
                                window.cacheInstance.setItem(pKey, p, ttl);
                            }
                            dbService.setItem(pKey, p).catch(() => {});
                        }
                    }
                }
            }
            return data;
        } catch (error) {
            if (error && error.code === 'UNAUTHORIZED') {
                throw error;
            }
            console.warn("fetchFavorites offline fallback:", error);
            const cached = localStorage.getItem('triad_offline_favorites');
            if (cached) {
                try {
                    return JSON.parse(cached);
                } catch (e) {
                    console.error("Error parsing cached favorites:", e);
                }
            }
            return [];
        }
    },
    fetchOwnedBy: (params) => {

        return PlaylistService.loadFromCache(OWNED_BY_PLAYLISTS_CACHE_KEY, params.force, () => {
            return fetch("/api/playlist/?owner=true", {
                signal: abortController.signal,
                method: "GET",
                headers: {
                    "content-type": "application/json"
                }
            });
        })
    },
    toggleFavorite: (id) => {
        return fetch("/api/playlist/" + id + "/favorite", {
            method: "PUT",
            headers: {
                "content-type": "application/json"
            }
        })
    },
    updateName: (id, name) => {
        PlaylistService.clearCache(PLAYLIST_CACHE_KEY.replace("[id]", id));
        return PlaylistService.withSnapshotNotify(id, fetch("/api/playlist/" + id, {
            method: "PUT",
            body: JSON.stringify({ name: name }),
            headers: {
                "content-type": "application/json"
            }
        }))
    },
    updateTags: (id, tags) => {
        PlaylistService.clearCache(PLAYLIST_CACHE_KEY.replace("[id]", id));
        return PlaylistService.withSnapshotNotify(id, fetch("/api/playlist/" + id + "/tags", {
            method: "PUT",
            body: JSON.stringify({ tags: tags }),
            headers: {
                "content-type": "application/json"
            }
        }))
    },
    updateImage: (id, image) => {
        PlaylistService.clearCache(PLAYLIST_CACHE_KEY.replace("[id]", id));
        PlaylistService.invalidateServiceWorkerCache(id);
        return PlaylistService.withSnapshotNotify(id, fetch("/api/playlist/" + id + "/image", {
            method: "POST",
            body: JSON.stringify({ image: image }),
            headers: {
                "content-type": "application/json"
            }
        }))
    },
    updateDescription: (id, description) => {
        PlaylistService.clearCache(PLAYLIST_CACHE_KEY.replace("[id]", id));
        return PlaylistService.withSnapshotNotify(id, fetch("/api/playlist/" + id + "/description", {
            method: "POST",
            body: JSON.stringify({ description: description }),
            headers: {
                "content-type": "application/json"
            }
        }))
    },
    updatePlaylist: (playlist) => {
        const id = playlist._id || playlist.id;
        PlaylistService.clearCache(PLAYLIST_CACHE_KEY.replace("[id]", id));
        return PlaylistService.withSnapshotNotify(id, fetch("/api/playlist/" + id, {
            method: "PUT",
            body: JSON.stringify(playlist),
            headers: {
                "content-type": "application/json"
            }
        }), playlist)
    },
    generateDescription: (lang, playlistData) => {
        return fetch("/api/ai/generate-description", {
            method: "POST",
            body: JSON.stringify({ lang, ...playlistData }),
            headers: {
                "content-type": "application/json"
            }
        })
    },
    generateCover: (payload) => {
        return fetch("/api/ai/generate-cover", {
            method: "POST",
            body: JSON.stringify(payload),
            headers: {
                "content-type": "application/json"
            }
        })
    }
}

export default PlaylistService;
